/**
 * This file contains all the endpoints used in the application.
 * The endpoints are grouped by the resource they are related to (mostly).
 *
 * The endpoint suffixed by "_INDEXED" mark that the endpoint is not querying
 * the DB directly, but an search indexing service instead.
 */

const APPLICATIONS_BASE = "/api/int/applications";
const ATTRIBUTES_BASE = "/api/int/attributes";
const AI_AGENTS_BASE = "/api/int/ai_agents";
const CLIENTS_BASE = "/api/int/clients";
const COMMENTS_BASE = "/api/int/comments";
const CONTRACTS_BASE = "/api/int/docusign_placeholders";
const CONTRACT_TEMPLATES = "/api/int/contract_templates";
const BUSINESSES_BASE = "/api/int/business_profiles";
const PROFILES_BASE = "/api/int/profiles";
const PROFILES_BASE_V2 = "/api/v2/profiles";
const OFFERS_BASE = "/api/int/offers";
const FUNDERS_BASE = "/api/int/funders";
const USERS_BASE = "/api/int/users";
const DATA_ORCHESTRATION_BASE = "/api/int/data_orchestration_templates";
const WORKFLOW_BASE = "/api/int/workflow_templates";
const SCORECARDS_BASE = "api/int/score_cards";
const SCORECARD_GROUPS_BASE = "api/int/score_card_groups";
const FILTERS_BASE = "/api/int/filters";
const AUTH_BASE = "/api/int/auth";
const BASE = "/api/int";
const APPLICATION_ATTRIBUTES_BASE = `${APPLICATIONS_BASE}/%s/attributes`;
const BANK_ACCOUNTS_BASE = `${APPLICATIONS_BASE}/%s/bank_accounts`;
const PRESETS_BASE = "/api/int/presets";
const BATCH_ACTIONS_BASE = "/api/int/batch_actions";
const EMAIL_TEMPLATES_BASE = "/api/int/email/template";
const OFFER_TEMPLATES_BASE = "/api/int/offer_templates";
const SMS_TEMPLATES_BASE = "/api/int/sms/template";
const CUSTOM_WEBHOOKS_BASE = "api/int/webhooks";
const BORROWER_PLATFORM_TEMPLATES_BASE = "/api/int/borrower_platform_templates";
const OPTIONS_BASE = "/api/int/options";
const COMMUNICATION_LOGS_BASE = "/api/int/communication_logs";
const EMAIL_BASE = "/api/int/communication/emails";
const SMS_BASE = "/api/int/communication/sms";
const MY_BASE = "/api/int/my";
const COMMUNICATION_BASE = "/api/int/communication";
const DOCUMENT_TEMPLATE_BASE = "/api/int/document_templates";
const CHECKOUT_TEMPLATE_BASE = "/api/int/checkout_templates";
const GMAIL_BASE = `${USERS_BASE}/%s/email_integrations/gmail`;
const TWILIO_BASE = "/api/int/twilio";
const CALL_SESSIONS_BASE = "/api/int/call_sessions";
const AI_AGENT_SESSIONS_BASE = "/api/int/ai_agent_sessions";
const SERVICES_BASE = "/api/int/services";
const KEYBOARD_SHORTCUTS_BASE = "/api/int/keyboard_shortcuts";
const WHITELABEL_BASE = `${CLIENTS_BASE}/%s/whitelabel`;

const endpoints = {
  REFRESH_TOKEN: "/api/int/auth/refresh",
  AUTH_DETAILS: "/api/int/auth/me",
  IMPERSONATE_USER: "/api/int/users/%s/impersonate",
  DOC_VERIFICATION: "/api/v1/applications/business_credit/%s/verify-documents",

  CSRF_TOKEN: "/api/csrf-token",

  LOGIN: "/api/auth/login",
  REGISTER: "/api/auth/register",
  SEND_RESET_LINK: "/api/password/email",
  RESET_PASSWORD: "/api/password/reset",
  RENEW_PASSWORD: "/api/password/renew",

  GET_OPTIONS: {
    ACTIVITY_HUB: {
      TABS: {
        ALL: `${OPTIONS_BASE}/activity_hub`,
        DEAL: `${OPTIONS_BASE}/activity_hub/%s`,
      },
    },
    AI_AGENTS: `${OPTIONS_BASE}/ai_agents`,
    ALL: `${OPTIONS_BASE}`,
    APPLICATION: `${OPTIONS_BASE}/application`,
    ATTRIBUTE: `${OPTIONS_BASE}/attributes`,
    CHECKOUT: `${OPTIONS_BASE}/checkout_templates`,
    CONSENT_TEMPLATES: `${BASE}/options/consent_templates`,
    DATA_ORCHESTRATION: `${OPTIONS_BASE}/data_orchestration`,
    DYNAMIC_FIELDS: `${OPTIONS_BASE}/dynamic_fields`,
    WORKFLOW_TEMPLATES: `${OPTIONS_BASE}/workflow_templates`,
    CONTRACT_TEMPLATES: `${OPTIONS_BASE}/contract_templates`,
    BORROWER_PLATFORM_TEMPLATES: `${OPTIONS_BASE}/borrower_platform_templates`,
    SINGLE_ATTRIBUTE_OPTIONS: "api/int/expressions/attributes/%s",
    OFFER: `${OPTIONS_BASE}/offer`,
    NAICS_CODES: `${OPTIONS_BASE}/naics_codes`,
    ACCESS_TOKENS: `${OPTIONS_BASE}/access_tokens`,
  },

  KEYBOARD_SHORTCUTS: {
    UPDATE: `${KEYBOARD_SHORTCUTS_BASE}`,
  },

  FILTERS: {
    ALL: FILTERS_BASE,
    CREATE: FILTERS_BASE,
    SINGLE: `${FILTERS_BASE}/%s`,
    VISIBILITY: `${FILTERS_BASE}/visibility`,
    SORT: `${FILTERS_BASE}/sort`,
  },

  APPLICATIONS: {
    AI: {
      SEARCH: `${APPLICATIONS_BASE}/ai-powered-search`,
      AGENTS: {
        SESSIONS: {
          ALL: AI_AGENT_SESSIONS_BASE,
          CREATE: AI_AGENT_SESSIONS_BASE,
          PROMPT: `${AI_AGENT_SESSIONS_BASE}/%s/prompt`,
          SINGLE: `${AI_AGENT_SESSIONS_BASE}/%s`,
          FILES: `${AI_AGENT_SESSIONS_BASE}/%s/files`,
        },
      },
    },
    GENERATE_TEMPORARY_LINKS_V2: "/api/v2/applications/%s/temporary_links",
    GET_APPLICATIONS: `${APPLICATIONS_BASE}`,
    GET_APPLICATIONS_INDEXED: `${APPLICATIONS_BASE}/filter`,
    DUPLCATE: `${APPLICATIONS_BASE}/%s/duplicate`,
    GET_APPLICATIONS_BUSINESS_CREDIT: `${APPLICATIONS_BASE}/business_credit`,
    SINGLE_APPLICATION: `${APPLICATIONS_BASE}/%s`,
    UPDATE_APP_INFO: `${APPLICATIONS_BASE}/%s`,
    UPDATE_BUSINESS_INFO: `${APPLICATIONS_BASE}/%s/business_info`,
    UPDATE_PERSONAL_INFO: `${APPLICATIONS_BASE}/%s/personal_info/%s`,
    DEAD: `${APPLICATIONS_BASE}/%s/dead`,
    SEND_DEAD_EMAIL: `${APPLICATIONS_BASE}/%s/declined_notification`,
    REVIVE: `${APPLICATIONS_BASE}/%s/revive`,
    UPDATE_STATUS: `${APPLICATIONS_BASE}/%s/status`,
    PLACEMENTS: `${APPLICATIONS_BASE}/%s/placements`,
    UPDATE_PLACEMENT: `${APPLICATIONS_BASE}/%s/placements/%s`,
    UPLOAD_PLACEMENT_DOCS: `${APPLICATIONS_BASE}/%s/placements/%s/upload_documents`,
    ATTACH_PLACEMENT_DOCS: `${APPLICATIONS_BASE}/%s/placements/%s/attach_documents`,
    RESUBMIT_PLACEMENT: `${APPLICATIONS_BASE}/%s/placements/%s/resubmit`,
    PLACEMENTS_FOR_DEALS: "api/int/placements",
    OFFERS_FOR_DEALS: "api/int/offers",
    CONTACTED_VIA: `${APPLICATIONS_BASE}/%s/offer_contacted_via`,
    PLACEMENT_STATUS: `${APPLICATIONS_BASE}/%s/placements/status`,
    FILES: {
      SINGLE: `${APPLICATIONS_BASE}/%s/files`,
      MULTIPLE: `${APPLICATIONS_BASE}/%s/files/multiple`,
      REQUIRED: `${APPLICATIONS_BASE}/%s/required_documents`,
      SYNC_WITH_FUNDERS: `${APPLICATIONS_BASE}/%s/files/sync_with_funders`,
    },
    APP_OFFERS: `${APPLICATIONS_BASE}/%s/offers`,
    UPDATE_OFFERS: `${APPLICATIONS_BASE}/%s/offers/%s`,
    COUNT: `${APPLICATIONS_BASE}/count`,
    DISCOVERY_CALL_QUESTION: `${APPLICATIONS_BASE}/%s/questions`,
    UPDATE_DISCOVERY_CALL_QUESTION: `${APPLICATIONS_BASE}/%s/questions/%s`,
    UPDATE_DISCOVERY_CALL_ANSWERS: `${APPLICATIONS_BASE}/%s/questions/answer`,
    UPDATE_CLOSING_STATUS: `${APPLICATIONS_BASE}/%s/closing/status`,
    COMMERCIAL_DATA: `${APPLICATIONS_BASE}/%s/commercial_data`,
    SEND_CONTRACT_BY_EMAIL: `${APPLICATIONS_BASE}/%s/send_contract`,
    SEND_FUNDING_CONTRACT: `${APPLICATIONS_BASE}/%s/funding_contract`,
    GENERATE_BANK_DOCUMENT_REPORTS: `${APPLICATIONS_BASE}/%s/bank_report`,
    GET_LINK: `${APPLICATIONS_BASE}/%s/links`,
    GENERATE_TEMPORARY_LINKS: `${APPLICATIONS_BASE}/%s/temporary_links`,
    ENRICH_APP: "/api/int/applications/%s/enrich",
    ENRICH_BUSINESS_CREDIT_APP:
      "/api/v1/applications/business_credit/%s/enrich",
    BUSINESS_CREDIT_STATISTICS_BY_SERVICE:
      "/api/int/business_credit/statistics/by-service",
    BUSINESS_CREDIT_STATISTICS_BY_DATE:
      "/api/int/business_credit/statistics/by-date",
    ADVANCED_OCROLUS_CFA: `${APPLICATIONS_BASE}/%s/cfa/ocrolus/%s/analytics`,
    MONEYTHUMB_ANALYTICS: `${APPLICATIONS_BASE}/%s/cfa/moneythumb/%s/analytics`,
    CLIENT_PREFERENCES: "api/int/clients/%s/preferences",
    UPDATE_PIIS: `${APPLICATIONS_BASE}/%s/piis`,
    EQUIPMENT_RENTAL_APPROVAL: `${APPLICATIONS_BASE}/%s/equipment_rental_approval`,
    APPLICATION_ATTRIBUTES: APPLICATION_ATTRIBUTES_BASE,
    APPLICATION_ATTRIBUTES_ID: `${APPLICATION_ATTRIBUTES_BASE}/%s`,
    METRICS: `${APPLICATIONS_BASE}/%s/metrics`,
    DATA_ORCHESTRATION_BATCH_EXECUTE: `${BATCH_ACTIONS_BASE}/applications/data_orchestration/execute`,
    DATA_ORCHESTRATION_EXECUTE: `${APPLICATIONS_BASE}/%s/data_orchestration/execute`,
    DATA_ORCHESTRATION_PASS_RESUME: `${APPLICATIONS_BASE}/%s/data_orchestration/%s/continue_pass`,
    DATA_ORCHESTRATION_FAIL_RESUME: `${APPLICATIONS_BASE}/%s/data_orchestration/%s/continue_fail`,
    DATA_ORCHESTRATION_RETRY_STEP: `${APPLICATIONS_BASE}/%s/data_orchestration/%s/continue_retry`,
    DATA_ORCHESTRATION_MASTER_STATUS: `${APPLICATIONS_BASE}/%s/data_orchestration/%s/master_status`,
    SINGLE_DATA_ORCHESTRATION_LOG: `${APPLICATIONS_BASE}/%s/data_orchestration_logs/%s`,
    DATA_ORCHESTRATION_LOGS: `${APPLICATIONS_BASE}/%s/data_orchestration_logs`,
    DEAL_MODE: `${APPLICATIONS_BASE}/%s/deal_mode`,
    UPDATE_APPLICATION_STIPS: `${APPLICATIONS_BASE}/%s/stips`,
    STIPS_COLLECTION_LINK: `${APPLICATIONS_BASE}/%s/stips_collection_link`,
    BANK_TRANSACTIONS: `${APPLICATIONS_BASE}/%s/bank_transactions`,
    UNDERWRITING_LOGS: `${APPLICATIONS_BASE}/%s/underwriting_logs`,
    DATA_ORCHESTRATION_TEMPLATES: `${APPLICATIONS_BASE}/%s/data_orchestration_templates`,
    WIDGET_INFO: `${APPLICATIONS_BASE}/%s/widget_info`,
    PLAID_DATA: `${APPLICATIONS_BASE}/%s/plaid_data`,
    BANK_ACCOUNTS: {
      GET_BANK_ACCOUNTS: `${BANK_ACCOUNTS_BASE}`,
      DOWNLOAD: `${BANK_ACCOUNTS_BASE}/%s/download`,
      UPDATE_VISIBILITY: `${BANK_ACCOUNTS_BASE}/visibility`,
    },
    SEND_EMAIL: `${APPLICATIONS_BASE}/%s/outreach-webhook`,
    SCORECARD_RESULTS: `${APPLICATIONS_BASE}/%s/score_cards/results`,
    APPLICATION_FILES: `${APPLICATIONS_BASE}/%s/files`,
    UPDATE_SERVICE_JSON: "/internal/applications/%s/update_enrichment_response",
    SERVICES_OPTIONS: "/api/int/services_options",
    VALIDATE_PHONES: `${APPLICATIONS_BASE}/%s/validate_numbers`,
    WEBHOOKS: {
      TRIGGER_CUSTOM_WEBHOOK: `${APPLICATIONS_BASE}/%s/trigger_custom_webhook`,
    },
    SIGNED_DOCUMENTS: `${APPLICATIONS_BASE}/%s/signed_documents`,
    OFFERS_SHARED: `${APPLICATIONS_BASE}/%s/offers/toggle_shared`,
    EXTRACT_DATA: `${APPLICATIONS_BASE}/%s/document/extract_data`,
    FIELD_SOURCES: `${APPLICATIONS_BASE}/%s/field_sources`,
    PHONE_NUMBERS: `${APPLICATIONS_BASE}/%s/phone_numbers`,
    EMAILS: `${APPLICATIONS_BASE}/%s/emails`,
    TRACKING_TOKENS: {
      ALL: `${APPLICATIONS_BASE}/%s/additional_tracking_tokens`,
      SINGLE: `${APPLICATIONS_BASE}/%s/additional_tracking_tokens/%s`,
    },
    METADATA: {
      ALL: `${APPLICATIONS_BASE}/%s/metadata`,
      SINGLE: `${APPLICATIONS_BASE}/%s/metadata/%s`,
      SYNC_WITH_FUNDERS: `${APPLICATIONS_BASE}/%s/metadata/sync_with_funders`,
    },
    REFERRER_HOST: `${BASE}/referrer_hosts`,
  },

  AI_AGENTS: {
    INDEX: AI_AGENTS_BASE,
    SINGLE: `${AI_AGENTS_BASE}/%s`,
    VISIBILITY: {
      USER: `${AI_AGENTS_BASE}/visibility/user`,
      CLIENT: `${AI_AGENTS_BASE}/visibility/client`,
    },
  },

  AUTH: {
    ACL: `${AUTH_BASE}/acl`,
    ABILITY: `${AUTH_BASE}/can`,
    SETTINGS: "/api/auth/settings",
  },

  ATTRIBUTES: {
    BASE: `${ATTRIBUTES_BASE}`,
    SINGLE: `${ATTRIBUTES_BASE}/%s`,
    MANY: `${ATTRIBUTES_BASE}/many`,
    SERVICES: `${ATTRIBUTES_BASE}/services`,
    EVALUATE: `${ATTRIBUTES_BASE}/evaluate`,
  },

  CONTRACTS: {
    BASE: `${CONTRACTS_BASE}`,
    SINGLE: `${CONTRACTS_BASE}/%s`,
  },

  CONTRACT_TEMPLATES: {
    BASE: CONTRACT_TEMPLATES,
    SINGLE: `${CONTRACT_TEMPLATES}/%s`,
    DUPLICATE: `${CONTRACT_TEMPLATES}/%s/duplicate`,
    UPLOAD_DOCUMENT: `${CONTRACT_TEMPLATES}/%s/upload_document`,
    DELETE_DOCUMENT: `${CONTRACT_TEMPLATES}/%s/document/%s`,
  },

  COMMENTS: {
    INDEX: COMMENTS_BASE,
    SINGLE: `${COMMENTS_BASE}/%s`,
    RESOLVE: `${COMMENTS_BASE}/%s/resolve`,
    UNRESOLVE: `${COMMENTS_BASE}/%s/unresolve`,
  },

  OFFERS: {
    SINGLE_OFFER: `${OFFERS_BASE}/%s`,
    UPDATE_OFFER_STIPS: `${OFFERS_BASE}/%s/stips`,
    UPDATE_OFFER_STATUS: `${OFFERS_BASE}/%s/status`,
    SEND_STIPS_TO_FUNDER: `${OFFERS_BASE}/%s/send_stips_to_funder`,
    CONFIRM_OFFER: `${OFFERS_BASE}/%s/confirm`,
    DELETE_OFFER: `${APPLICATIONS_BASE}/%s/offers/%s`,
    DUPLICATE_OFFER: `${OFFERS_BASE}/%s/duplicate`,
    CLIENT_FUNDED_OFFER: `${APPLICATIONS_BASE}/%s/client_funded_offer`,
    SEND_OFFERS_EMAIL: `${APPLICATIONS_BASE}/%s/offers/email_applicant`,
    CREATE_EQUIPMENT_RENTAL_OFFER: `${APPLICATIONS_BASE}/%s/equipment_rental_offer`,
    UPDATE_EQUIPMENT_RENTAL_OFFER: `${APPLICATIONS_BASE}/%s/equipment_rental_offer/%s`,
    FUNDING_TRANSACTIONS: `${OFFERS_BASE}/%s/funding_transactions`,
    SINGLE_FUNDING_TRANSACTION: `${OFFERS_BASE}/%s/funding_transactions/%s`,
    UPLOAD_TRANSACTION_FILE: `${OFFERS_BASE}/%s/funding_transactions/%s/upload`,
    STIPS_COLLECTION_LINK: `${OFFERS_BASE}/%s/stips_collection_link`,
    SEND_TO_LOAN_PROVIDER: `${OFFERS_BASE}/%s/loan_providers/%s/merchant`,
    OFFER_NOTE: `${OFFERS_BASE}/%s/note`,
    OFFER_DETAILS: `${OFFERS_BASE}/%s/details`,
    TOGGLE_SHOW_OFFER: `${OFFERS_BASE}/%s/toggle_shared`,
    PLACEMENTS_OFFERS: "/api/int/placements/%s/offer_details",
    SEND_OFFER_AVAILABLE_WEBHOOK: `${OFFERS_BASE}/%s/send_available_webhook`,
    CHECKOUT: {
      TEMPLATE: `${OFFERS_BASE}/%s/checkout`,
      PROGRESS: `${OFFERS_BASE}/%s/checkout/progress`,
    },
  },

  BUSINESSES: {
    ALL: BUSINESSES_BASE,
    SINGLE: `${BUSINESSES_BASE}/%s`,
    APPLICATIONS: `${BUSINESSES_BASE}/%s/applications`,
    LAST_APPLICATION: `${BUSINESSES_BASE}/%s/last_application`,
    BUSINESS_RELATIONS: `${BUSINESSES_BASE}`,
  },

  PROFILES: {
    CREATE_PROFILE: PROFILES_BASE_V2,
    ALL_BY_TYPE: `${PROFILES_BASE}/%s`,
    DEALS: `${PROFILES_BASE}/%s/%s/applications`,
    ENTITY_OWNERSHIP_SNAPSHOT: `${PROFILES_BASE}/%s/%s/ownership`,
    LATEST_ENTITY_OWNERSHIP_SNAPSHOT: `${PROFILES_BASE}/%s/%s/ownership/latest`,
    RELATIONS: `${PROFILES_BASE}/%s/%s/relationships`,
    SINGLE_BY_TYPE: `${PROFILES_BASE}/%s/%s`,
  },

  EVALUATE: {
    EXPRESSIONS: `${BASE}/expressions/evaluate`,
  },

  FILES: {
    OFFER_STIP: "/api/int/files/%s/offer_stip",
    SINGLE: "/api/int/files/%s",
    ALL: "/api/int/files",
    UPDATE: "/api/int/applications/%s/files/%s",
    DETACH_STIPS: "/api/int/files/%s/stips",
  },

  FUNDERS: {
    CREATE_FUNDER: FUNDERS_BASE,
    UPDATE_FUNDER: `${FUNDERS_BASE}/%s`,
    SINGLE_FUNDER: `${FUNDERS_BASE}/%s`,
    GET_FUNDERS: FUNDERS_BASE,
    APPROVE_FUNDER: `${FUNDERS_BASE}/%s/approve`,
    GET_FUNDER_STATISTICS: `${FUNDERS_BASE}/%s/statistics`,
    GET_USERS: `${FUNDERS_BASE}/%s/users`,
    LOGO: `${FUNDERS_BASE}/%s/logo`,
  },

  CLIENTS: {
    ALL: `${CLIENTS_BASE}`,
    SINGLE: `${CLIENTS_BASE}/%s`,
    UPDATE_CLIENT_PIIS: `${CLIENTS_BASE}/%s/piis`,
    GET_PLAID_LINK_TOKEN: `${CLIENTS_BASE}/%s/plaid/link-token`,
    SET_PLAID_TOKEN: `${CLIENTS_BASE}/%s/plaid/token`,
    UPLOAD_FILES: `${CLIENTS_BASE}/%s/files`,
    CLIENT_USERS: `${CLIENTS_BASE}/%s/users`,
    NOTIFICATIONS: `${CLIENTS_BASE}/%s/notifications`,
    WIDGET_LOGO: `${CLIENTS_BASE}/%s/widget_logo`,
    INTEGRATION: `${CLIENTS_BASE}/%s/integration`,
    INTEGRATION_BORROWER_PLATFORM: `${CLIENTS_BASE}/%s/integration/borrower_platform`,
    INTEGRATION_WIDGET: `${CLIENTS_BASE}/%s/integration/widget`,
    EXPERIAN_SUBCODES: `${CLIENTS_BASE}/%s/experian_subcodes`,
    CREDENTIALS: `${CLIENTS_BASE}/%s/service_credentials`,
    CREDENTIALS_RULES: `${CLIENTS_BASE}/%s/service_credentials_rules`,
    CREDENTIALS_DOCUSIGN_CONSENT: `${CLIENTS_BASE}/%s/integration/docusign_consent`,
    WEBHOOK_SUPPORT: `${CLIENTS_BASE}/%s/service_credentials_webhook_support`,
    CONFIGURE_WEBHOOK: `${CLIENTS_BASE}/%s/configure-webhook/%s`,
    FUNDER_INTEGRATIONS: `${CLIENTS_BASE}/%s/integration/funders`,
    FUNDER_INTEGRATION_SINGLE: `${CLIENTS_BASE}/%s/integration/funders/%s`,
    UPDATE_CLOSE_IO: `${CLIENTS_BASE}/%s/closeio`,
    EMAIL: {
      BRANDING: {
        SET_ACTIVE_TEMPLATE: `${CLIENTS_BASE}/%s/email_customization/%s/active`,
        CUSTOMISATION_DETAILS: `${CLIENTS_BASE}/%s/email_customization/%s`,
        IMAGE: `${CLIENTS_BASE}/%s/email_customization/%s/upload`,
        EMAIL_TEMPLATES: `${CLIENTS_BASE}/%s/email_customization`,
        HEADER_IMAGE: `${CLIENTS_BASE}/%s/email_customization/%s/header_image`,
        LOGO: `${CLIENTS_BASE}/%s/email_customization/%s/logo`,
      },
      RECIPIENTS: `${CLIENTS_BASE}/%s/recipients`,
    },
    CUSTOM_WEBHOOKS: {
      ALL: `${CUSTOM_WEBHOOKS_BASE}`,
      SINGLE: `${CUSTOM_WEBHOOKS_BASE}/%s`,
      TRIGGER: `${CUSTOM_WEBHOOKS_BASE}/trigger`,
      DUPLICATE: `${CUSTOM_WEBHOOKS_BASE}/%s/duplicate`,
    },
    BORROWER_PLATFORM_TEMPLATES: {
      ALL: `${BORROWER_PLATFORM_TEMPLATES_BASE}`,
      SINGLE: `${BORROWER_PLATFORM_TEMPLATES_BASE}/%s`,
      TOGGLE_GLOBAL: `${BORROWER_PLATFORM_TEMPLATES_BASE}/%s/toggle_global_status`,
      FILES: `${BORROWER_PLATFORM_TEMPLATES_BASE}/%s/files`,
      SINGLE_FILE: `${BORROWER_PLATFORM_TEMPLATES_BASE}/%s/files/%s`,
    },
    SALES_REPRESENTATIVES: `${CLIENTS_BASE}/%s/sales_representatives`,
    SINGLE_SALES_REPRESENTATIVE: `${CLIENTS_BASE}/%s/sales_representatives/%s`,
    WIDGET_TEMPLATES: `${CLIENTS_BASE}/%s/widget_templates`,
    WIDGET_TEMPLATE: `${CLIENTS_BASE}/%s/widget_templates/%s`,
    WIDGET_TEMPLATE_LOGO: `${CLIENTS_BASE}/%s/widget_templates/%s/logo`,
    TEST_EMAIL: `${CLIENTS_BASE}/%s/test_aec`,
    FUNDER_LINK: `${CLIENTS_BASE}/%s/link_to_funder`,
    TEST_MODE: `${CLIENTS_BASE}/%s/test_mode`,
    MASKED_PIIS: `${CLIENTS_BASE}/%s/masked_piis`,
    EMBED_REPORT_TOKEN: `${CLIENTS_BASE}/%s/embed_report_token`,
    TRACKING_TOKENS: `${BASE}/tracking_tokens`,
    PIIS: `${CLIENTS_BASE}/%s/piis/%s/deobfuscate`,
    ADDITIONAL_TRACKING_TOKENS: `${BASE}/additional_tracking_tokens`,
    UTM_PARAMETERS: `${BASE}/utm_parameters`,
    WHITELABEL: {
      ALL: `${WHITELABEL_BASE}`,
      SINGLE: `${WHITELABEL_BASE}/%s`,
      REORDER: `${WHITELABEL_BASE}/reorder`,
      DNS_RECORDS: `${WHITELABEL_BASE}/%s/dns_records`,
      DOMAIN_STATUS: `${WHITELABEL_BASE}/%s/domain_status`,
      ALIASES: {
        ALL: `${WHITELABEL_BASE}/%s/aliases`,
        SINGLE: `${WHITELABEL_BASE}/%s/aliases/%s`,
      },
    },
    EXPLO: {
      DASHBOARD: {
        ALL: `${BASE}/explo/dashboards`,
      },
    },
  },

  OFFER_TEMPLATES: {
    ALL: OFFER_TEMPLATES_BASE,
    SINGLE_TEMPLATE: `${OFFER_TEMPLATES_BASE}/%s`,
    CONTRACT_TEMPLATE_FIELDS: `${OFFER_TEMPLATES_BASE}/%s/contract_template_fields`,
  },

  USERS: {
    ALL: USERS_BASE,
    SINGLE: `${USERS_BASE}/%s`,
    EMPLOYEES: `${USERS_BASE}/lendflow`,
    NOTIFICATIONS: `${USERS_BASE}/%s/notifications`,
    COLUMN_PREFERENCES: "api/int/my/preferences",
    FILTER_BY_ROLE: `${USERS_BASE}/filter_by_role`,
    RESET_RING_CENTRAL: `${USERS_BASE}/%s/reset-ring-central`,
    EMAIL_ADDRESSES: {
      ALL: `${USERS_BASE}/%s/email_addresses`,
      UPLOAD_IMAGE: `${USERS_BASE}/%s/email_addresses/upload_image`,
    },
    FETCH_GOOGLE_TOKENS: `${GMAIL_BASE}/fetch_tokens`,
    DELETE_GOOGLE_TOKENS: `${GMAIL_BASE}`,
    FETCH_TWILIO_TOKEN: `${TWILIO_BASE}/access-token`,
  },
  TWILIO: {
    PHONE_NUMBER_MANAGEMENT: {
      AVAILABLE: `${TWILIO_BASE}/phone-numbers-management/available`,
      ASSIGNED: `${TWILIO_BASE}/phone-numbers-management/%s/assigned`,
      MANAGE: `${TWILIO_BASE}/phone-numbers-management/manage`,
    },
  },

  WORKFLOW_TEMPLATES: {
    ALL: `${WORKFLOW_BASE}`,
    FILTER: "/api/int/workflows/filter",
    FILTER_TEMPLATES: "/api/int/filter_workflow_templates",
    SINGLE: `${WORKFLOW_BASE}/%s`,
    TOGGLE_GLOBAL: `${WORKFLOW_BASE}/%s/toggle_global_status`,
    SUBMIT_TO_NEXT_STAGE: `${APPLICATIONS_BASE}/%s/workflow/stages/next`,
    ROLLBACK_STAGE_PROGRESS: `${APPLICATIONS_BASE}/%s/workflow/stages/rollback`,
    CHANGE_OFFER_TEMPLATES_WEIGHT: `${APPLICATIONS_BASE}/%s/workflow/offer_templates/weight`,
    WORKFLOW_SNAPSHOT: `${APPLICATIONS_BASE}/%s/workflow`,
    WORKFLOW_STATUS: `${APPLICATIONS_BASE}/%s/workflow_status`,
    WORKFLOW_DISCOVERY_QUESTIONS: `${APPLICATIONS_BASE}/%s/workflow/discovery_questions`,
    WORKFLOW_DISCOVERY_ANSWERS: `${APPLICATIONS_BASE}/%s/workflow/discovery_questions/answer`,
    WORKFLOW_FIELDS_DATA: `${APPLICATIONS_BASE}/%s/workflow/stages/application/fields`,
    UPDATE_APP: `${APPLICATIONS_BASE}/%s/workflow`,
    DUPLICATE_TEMPLATE: `${WORKFLOW_BASE}/%s/duplicate`,
  },

  EMAIL_TEMPLATES: {
    ALL: `${EMAIL_TEMPLATES_BASE}`,
    SINGLE: `${EMAIL_TEMPLATES_BASE}/%s`,
    DUPLICATE: `${EMAIL_TEMPLATES_BASE}/%s/duplicate`,
    TEST_DATA: `${EMAIL_TEMPLATES_BASE}/test_data`,
    TEST_SEND: `${EMAIL_TEMPLATES_BASE}/test_send`,
  },

  SMS_TEMPLATES: {
    ALL: `${SMS_TEMPLATES_BASE}`,
    SINGLE: `${SMS_TEMPLATES_BASE}/%s`,
    DUPLICATE: `${SMS_TEMPLATES_BASE}/%s/duplicate`,
    TEST_DATA: `${SMS_TEMPLATES_BASE}/test_data`,
    TEST_SEND: `${SMS_TEMPLATES_BASE}/test_send`,
  },

  STIPS: {
    ALL: "/api/int/stips",
    SINGLE: "/api/int/stips/%s",
  },

  DATA_ORCHESTRATION_TEMPLATES: {
    ALL: `${DATA_ORCHESTRATION_BASE}`,
    SINGLE: `${DATA_ORCHESTRATION_BASE}/%s`,
    SERVICES: `${DATA_ORCHESTRATION_BASE}/services`,
  },

  PRESETS: {
    ALL: PRESETS_BASE,
    SINGLE: `${PRESETS_BASE}/%s`,
  },

  SCORECARDS: {
    ALL: `${SCORECARDS_BASE}`,
    BACKTEST_REPORTS: {
      ALL: `${BASE}/score_card_backtest_report`,
      SINGLE: `${BASE}/score_card_backtest_report/%s`,
    },
    DUPLICATE: `${SCORECARDS_BASE}/%s/duplicate`,
    MULTI_RESULTS: `${SCORECARDS_BASE}/%s/multi-results`,
    PRIMARY: `${APPLICATIONS_BASE}/%s/score_cards/%s/primary`,
    RESULTS: `${SCORECARDS_BASE}/%s/results`,
    RUN_BACKTEST_REPORT: `${SCORECARDS_BASE}/%s/backtest_report`,
    RUN_SCORECARDS: `${APPLICATIONS_BASE}/%s/run_score_cards`,
    SERVICES: `${SCORECARDS_BASE}/services`,
    SINGLE: `${SCORECARDS_BASE}/%s`,
    UPDATE_POINTS: `${APPLICATIONS_BASE}/%s/score_cards/%s/points`,
  },

  SCORECARD_GROUPS: {
    ALL: `${SCORECARD_GROUPS_BASE}`,
    BACKTEST_REPORT: `${SCORECARD_GROUPS_BASE}/%s/backtest_report`,
    MULTI_RESULTS: `${SCORECARD_GROUPS_BASE}/%s/multi-results`,
    PRIMARY: `${APPLICATIONS_BASE}/%s/score_card_groups/%s/primary`,
    RUN_SCORECARD_GROUPS: `${APPLICATIONS_BASE}/%s/run_score_card_groups`,
    SINGLE: `${SCORECARD_GROUPS_BASE}/%s`,
    RESULTS: `${SCORECARD_GROUPS_BASE}/%s/results`,
  },

  COMMUNICATION: {
    CALL_OUT: `${APPLICATIONS_BASE}/%s/call_out`,
    APP_LOGGING: `${APPLICATIONS_BASE}/%s/communications`,
    LOGS: {
      ALL: COMMUNICATION_LOGS_BASE,
      ALL_INDEXED: `${COMMUNICATION_LOGS_BASE}/filter`,
      SINGLE: `${COMMUNICATION_LOGS_BASE}/%s`,
    },
    TASKS: {
      CREATE: `${BASE}/tasks`,
      UPDATE: `${BASE}/tasks/%s`,
    },
    CALL_LOGS: {
      CREATE: `${APPLICATIONS_BASE}/%s/manual_call`,
      UPDATE: `${CALL_SESSIONS_BASE}/%s`,
      UPLOAD_RECORDING: `${CALL_SESSIONS_BASE}/%s/upload_recording`,
      ASSIGN_TO_APPLICATION: `${CALL_SESSIONS_BASE}/%s/assign`,
      TEXT: {
        SUMMARY: {
          ALL: `${CALL_SESSIONS_BASE}/%s/summaries`,
          SINGLE: `${CALL_SESSIONS_BASE}/%s/summaries/%s`,
          CREATE: `${CALL_SESSIONS_BASE}/%s/summaries`,
        },
        TRANSCRIPTION: {
          ALL: `${CALL_SESSIONS_BASE}/%s/transcriptions`,
          SINGLE: `${CALL_SESSIONS_BASE}/%s/transcriptions/%s`,
          CREATE: `${CALL_SESSIONS_BASE}/%s/transcriptions`,
        },
      },
    },
    COMMENTS: {
      /**
       * Placeholder 1 = "call_sessions" OR "tasks"
       * Placeholder 2 = "call_sessions" OR "tasks" ID
       * Placeholder 3 (Optional) = Comment ID
       */
      ALL: `${BASE}/%s/%s/comments`,
      SINGLE: `${BASE}/%s/%s/comments/%s`,
    },
    EMAILS: {
      SEND: EMAIL_BASE,
      MODAL_DATA: `${EMAIL_BASE}/modal_data`,
      ASSIGN_TO_APPLICATION: `${EMAIL_BASE}/%s/assign`,
      THREADS: `${EMAIL_BASE}/threads`,
      THREAD: `${EMAIL_BASE}/threads/%s`,
      REPLY: `${EMAIL_BASE}/%s/reply`,
      RECIPIENTS: `${EMAIL_BASE}/recipients`,
    },
    SMS: {
      ASSIGN_TO_APPLICATION: `${SMS_BASE}/%s/assign`,
      CONVERSATION: `${SMS_BASE}/conversations/%s`,
      REPLY: `${SMS_BASE}/%s/reply`,
      MODAL_DATA: `${SMS_BASE}/modal_data`,
      SEND: `${SMS_BASE}`,
    },
    PHONES: {
      BLOCK: `${COMMUNICATION_BASE}/%s/block-number`,
      UNBLOCK: `${COMMUNICATION_BASE}/%s/unblock-number`,
    },
    STANDALONE: {
      CALLS: `${COMMUNICATION_BASE}/calls/standalone`,
      SMS: `${COMMUNICATION_BASE}/sms/standalone`,
    },
  },

  BATCH_ACTIONS: {
    DEAL_MODE: `${BATCH_ACTIONS_BASE}/applications/deal_mode`,
    ROLES: `${BATCH_ACTIONS_BASE}/applications/roles`,
    WORKFLOW_STATUS: {
      ALL: `${BATCH_ACTIONS_BASE}/applications/workflow_status/options`,
      UPDATE: `${BATCH_ACTIONS_BASE}/applications/workflow_status`,
    },
  },

  LENDER_NETWORKS: {
    INDEX: "/api/int/lender_networks",
    SHOW: "/api/int/lender_networks/%s",
  },

  MY: {
    ORGANIZATION_USERS: `${MY_BASE}/organization/users`,
    PREFERENCES: `${MY_BASE}/preferences`,
  },

  NOTES: {
    ALL: `${BASE}/notes`,
    SINGLE: `${BASE}/notes/%s`,
    MARK_AS_READ: `${BASE}/notes/mark_as_read`,
    PIN: `${BASE}/notes/%s/pin`,
    UNPIN: `${BASE}/notes/%s/unpin`,
  },

  CONSENT_TEMPLATE: {
    ALL: `${BASE}/consent_templates`,
    SINGLE: `${BASE}/consent_templates/%s`,
  },

  DOCUMENT_TEMPLATES: {
    ALL: DOCUMENT_TEMPLATE_BASE,
    CREATE: DOCUMENT_TEMPLATE_BASE,
    SINGLE: `${DOCUMENT_TEMPLATE_BASE}/%s`,
  },

  CHECKOUT_TEMPLATES: {
    ALL: CHECKOUT_TEMPLATE_BASE,
    SINGLE: `${CHECKOUT_TEMPLATE_BASE}/%s`,
  },

  SERVICES: {
    CHART: {
      GET_CLIENT_ID: `${SERVICES_BASE}/chart`,
    },
  },

  ACCESS_TOKENS: {
    ALL: `${BASE}/access_tokens`,
    SINGLE: `${BASE}/access_tokens/%s`,
  },

  STATISTIC: "/api/int/account/statistic",
  ANALYTICS: "/api/int/operations/analytics",
  SYSTEM_PREFERENCES: "api/int/system_preferences",
  DEAL_VISIBILITY: "api/int/system_preferences/deal_visibility",
  WEBSOCKET_AUTH: "/broadcasting/auth",
  KEY_VALUE: "/api/int/kv",
};

const endpointsRequiringCSRFToken = [
  endpoints.LOGIN,
  endpoints.REGISTER,
  endpoints.SEND_RESET_LINK,
  endpoints.RESET_PASSWORD,
  endpoints.RENEW_PASSWORD,
];

export const requiresCSRFToken = (endpoint: string): boolean =>
  endpointsRequiringCSRFToken.includes(endpoint);

export default endpoints;
